import { getCurrentTime } from "@/utils/base";
import {
  GetDictionary,
  GetPatientList,
  GetTemList,
  PatchEditPatient,
  PostAddPatient,
  PostCepingContent,
} from "@/request/common";
import { CepingScanPayload } from "@/utils/ceping-scan";

type ScanHost = {
  $message?: {
    success?: (msg: string) => void;
    warning?: (msg: string) => void;
    error?: (msg: string) => void;
  };
};

type DictionaryItem = {
  id?: number | string;
  label?: string;
  value?: string;
};

type SyncResult = {
  patient: any;
  checkContent: any | null;
};

const HENAN_CHECK_TEMPLATE_NAME = "河南检查";

function toStringSafe(value: any) {
  return String(value || "").trim();
}

function matchByLabelOrValue(items: DictionaryItem[], keyword: string) {
  return (
    items.find((item) => toStringSafe(item.label).includes(keyword)) ||
    items.find((item) => toStringSafe(item.value).includes(keyword)) ||
    null
  );
}

async function getDictionaryItems(vCom: any, key: string): Promise<DictionaryItem[]> {
  const res = await GetDictionary(vCom, {
    params: {
      dictionary_key: key,
    },
  });
  return Array.isArray(res?.data) ? res.data : [];
}

async function getGenderOption(vCom: any, gender?: string): Promise<DictionaryItem | null> {
  const options = await getDictionaryItems(vCom, "性别");
  if (gender === "男" || gender === "女") {
    return matchByLabelOrValue(options, gender);
  }
  return null;
}

async function getIdCardTypeOption(vCom: any): Promise<DictionaryItem | null> {
  const options = await getDictionaryItems(vCom, "认证号类型");
  return matchByLabelOrValue(options, "身份证");
}

async function findPatientByIdCard(vCom: any, idCard: string): Promise<any | null> {
  const res = await GetPatientList(vCom, {
    params: {
      page: 1,
      search: idCard,
      limit: 200,
    },
  });
  const list = Array.isArray(res?.data) ? res.data : [];
  return list.find((item: any) => toStringSafe(item.uid) === idCard) || null;
}

function buildPatientCreatePayload(
  payload: CepingScanPayload,
  genderOption: DictionaryItem | null,
  idCardTypeOption: DictionaryItem | null
) {
  const createPayload: any = {
    name: payload.patientName || "扫码患者",
    source: {},
    uid: payload.idCard,
    gender: genderOption || undefined,
    uid_type: idCardTypeOption || undefined,
    birthday: payload.birthday || undefined,
    phone_number: "",
    address: [],
    agree_informed_form: true,
  };

  Object.keys(createPayload).forEach((key) => {
    if (createPayload[key] === undefined) {
      delete createPayload[key];
    }
  });

  return createPayload;
}

function buildPatientPatchPayload(
  existingPatient: any,
  payload: CepingScanPayload,
  genderOption: DictionaryItem | null,
  idCardTypeOption: DictionaryItem | null
) {
  const patchPayload: any = {};

  if (payload.patientName && payload.patientName !== existingPatient?.name) {
    patchPayload.name = payload.patientName;
  }

  if (payload.birthday && payload.birthday !== existingPatient?.birthday) {
    patchPayload.birthday = payload.birthday;
  }

  if (genderOption && existingPatient?.gender?.id !== genderOption.id) {
    patchPayload.gender = genderOption;
  }

  if (idCardTypeOption && existingPatient?.uid_type?.id !== idCardTypeOption.id) {
    patchPayload.uid_type = idCardTypeOption;
  }

  if (payload.idCard && payload.idCard !== existingPatient?.uid) {
    patchPayload.uid = payload.idCard;
  }

  return patchPayload;
}

function setHeightWeightToTemplateFields(fields: any[], height?: string, weight?: string) {
  const h = toStringSafe(height);
  const w = toStringSafe(weight);

  fields.forEach((item: any) => {
    const title = toStringSafe(item?.title);
    if (h && title.includes("身高")) {
      item.value = h;
      return;
    }
    if (w && title.includes("体重")) {
      item.value = w;
    }
  });
}

async function createHenanCheckContent(
  vCom: any,
  patientId: number | string,
  payload: CepingScanPayload
): Promise<any | null> {
  const temRes = await GetTemList(vCom, {
    params: {
      name: HENAN_CHECK_TEMPLATE_NAME,
      page: 1,
      ignore_permissions: "true",
    },
  });

  const template = temRes?.data?.[0];
  if (!template || !template.id) {
    throw new Error("missing henan check template");
  }

  const fields = JSON.parse(JSON.stringify(template.fields || []));
  setHeightWeightToTemplateFields(fields, payload.height, payload.weight);

  const createRes = await PostCepingContent(vCom, {
    collect_time: getCurrentTime("yyyy-MM-dd HH:mm:ss"),
    patient: patientId,
    data: fields,
    template: {
      id: template.id,
    },
  });

  return createRes?.data || null;
}

export async function syncPatientAndCreateCheckByScan(
  vCom: ScanHost,
  payload: CepingScanPayload
): Promise<SyncResult> {
  const idCard = toStringSafe(payload.idCard);
  if (!idCard) {
    throw new Error("missing id card");
  }

  const [genderOption, idCardTypeOption] = await Promise.all([
    getGenderOption(vCom, payload.gender),
    getIdCardTypeOption(vCom),
  ]);

  let patient = await findPatientByIdCard(vCom, idCard);
  if (patient && patient.id) {
    const patchPayload = buildPatientPatchPayload(
      patient,
      payload,
      genderOption,
      idCardTypeOption
    );
    if (Object.keys(patchPayload).length > 0) {
      const patchRes = await PatchEditPatient(vCom, patient.id, patchPayload);
      patient = patchRes?.data || patient;
    }
  } else {
    const createPayload = buildPatientCreatePayload(
      payload,
      genderOption,
      idCardTypeOption
    );
    const createRes = await PostAddPatient(vCom, createPayload);
    patient = createRes?.data;
  }

  if (!patient || !patient.id) {
    throw new Error("patient upsert failed");
  }

  const checkContent = await createHenanCheckContent(vCom, patient.id, payload);
  return {
    patient,
    checkContent,
  };
}
